import { ProfilePictureLight } from '@/components/profile-picture/profile-picture.light';
import { useGetPersonalInfo, useUpdatePersonalInfo, useUpdateProfilePicture } from '@/hooks/api';
import { birthdateValidator, parseDate } from '@/lib';
import { useForm } from '@deadcow-enterprises/react-forms';
import { Loader, Pencil, Restart } from '@nosinovacao/fe-icons';
import {
  Button,
  DatePicker,
  Dialog,
  Input,
  logger,
  useHasRequiredFormError,
  useI18n,
  Range,
} from '@nosinovacao/nosid-mfe-common';
import { useRef, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';
import z from 'zod';
import { type StepNavigation } from './models';

export function UpdatePersonalInfoSection({ goToMain }: Readonly<StepNavigation<never>>) {
  const { t } = useI18n();
  const { data: personalInfo } = useGetPersonalInfo();
  const { mutateAsync } = useUpdatePersonalInfo();
  const form = useForm<{
    name: string;
    birthdate: Date | null | undefined;
  }>(
    {
      name: {
        value: personalInfo?.DisplayName || '',
        id: 'name',
        validator: z
          .string()
          .trim()
          .min(1, 'Common.UpdatePersonalInfo.Errors.NameRequired')
          .max(150, 'Common.UpdatePersonalInfo.Errors.NameTooLong')
          .regex(/^[\p{L}\p{M}\s'\-.]*$/u, 'Common.UpdatePersonalInfo.Errors.NameInvalidChars'),
      },
      birthdate: {
        value: parseDate(personalInfo?.BirthDate),
        id: 'birthdate',
        validator: birthdateValidator,
      },
    },
    { onError: logger.logFormErrors.bind(logger) },
  );
  const hasRequiredErrors = useHasRequiredFormError(form);
  async function handleSubmit({ birthdate, name }: typeof form.value) {
    if (!personalInfo) return;
    await mutateAsync({
      ...personalInfo,
      DisplayName: name,
      BirthDate: birthdate?.toISOString() ?? null,
    });
    goToMain();
  }
  return (
    <div className="flex h-full flex-col items-center gap-6 pb-8">
      <ProfilePictureImageUploader>
        <ProfilePictureLight
          className="shrink-0"
          size={80}
          name={personalInfo?.DisplayName}
          src={personalInfo?.PamPictureUri}
        />
      </ProfilePictureImageUploader>
      <form
        className="flex size-full flex-col gap-6"
        onSubmit={form.handleSubmit(handleSubmit)}
        data-tid="form-update-personal-info"
      >
        <Input
          label={t('Common.UpdatePersonalInfo.Name')}
          id={form.ids.name ?? 'name'}
          skin="light"
          value={form.value.name}
          data-tid="input-name"
          onChange={(v) => {
            form.setValue('name', v.target.value);
          }}
          errors={form.errors.name}
        />
        <DatePicker
          label={t('Common.UpdatePersonalInfo.BirthDate')}
          isBirthday
          id={form.ids.birthdate ?? 'birthdate'}
          skin="light"
          value={form.value.birthdate}
          data-tid="input-birthdate"
          onChange={(v) => {
            form.setValue('birthdate', v);
          }}
          errors={form.errors.birthdate}
        />
        <Button
          skin="light"
          className="mt-auto"
          disabled={hasRequiredErrors || form.pristine || form.loading}
          data-tid="button-submit-personal-info"
        >
          {form.loading ? (
            <Loader width={24} height={24} />
          ) : (
            t('Common.UpdatePersonalInfo.Confirm')
          )}
        </Button>
      </form>
    </div>
  );
}

function ProfilePictureImageUploader({ children }: Readonly<{ children?: React.ReactNode }>) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { mutateAsync: updateProfilePicture, isPending } = useUpdateProfilePicture();
  const { t } = useI18n();
  const [newImage, setNewImage] = useState<string | null>(null);
  function openFilePicker() {
    inputRef.current?.click();
  }
  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    // Convert to data URL instead of blob URL to comply with CSP
    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result as string;
      setNewImage(dataUrl);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<{
    croppedArea: Area;
    croppedAreaPixels: Area;
  } | null>(null);

  function handleSnapRotation() {
    const steps = [180, 90, 0, -90];
    const closestStep = steps.reduce(
      (prev, curr) => (Math.abs(curr - rotation) < Math.abs(prev - rotation) ? curr : prev),
      steps[0],
    );
    const nextStep =
      rotation - closestStep < 15 && rotation - closestStep > -15
        ? steps[(steps.indexOf(closestStep) + 1) % steps.length]
        : closestStep;
    setRotation(nextStep);
  }

  function handleCropComplete(croppedArea: Area, croppedAreaPixels: Area) {
    setCroppedAreaPixels({ croppedArea, croppedAreaPixels });
  }

  async function getNewImage(): Promise<Blob | null> {
    if (!newImage || !croppedAreaPixels) return null;

    const image = new Image();
    image.src = newImage;

    await new Promise((resolve) => {
      image.onload = resolve;
    });

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) return null;

    const { croppedAreaPixels: pixelCrop } = croppedAreaPixels;

    // Calculate rotated canvas size
    const rotRad = (rotation * Math.PI) / 180;
    const sin = Math.abs(Math.sin(rotRad));
    const cos = Math.abs(Math.cos(rotRad));
    const imageWidth = image.width;
    const imageHeight = image.height;

    // Create a temp canvas with enough space for rotation
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');

    if (!tempCtx) return null;

    tempCanvas.width = imageWidth * cos + imageHeight * sin;
    tempCanvas.height = imageWidth * sin + imageHeight * cos;

    // Rotate the image
    tempCtx.translate(tempCanvas.width / 2, tempCanvas.height / 2);
    tempCtx.rotate(rotRad);
    tempCtx.drawImage(image, -imageWidth / 2, -imageHeight / 2);

    // Set final canvas size to 125x125
    canvas.width = 125;
    canvas.height = 125;

    // Draw the cropped area onto the final canvas, scaled to 125x125
    // Note: croppedAreaPixels coordinates are already relative to the rotated image
    ctx.drawImage(
      tempCanvas,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      125,
      125,
    );

    // Convert to blob
    return new Promise((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', 0.95);
    });
  }

  async function handleConfirm() {
    if (!croppedAreaPixels) {
      return;
    }
    const imgBlob = await getNewImage();
    if (!imgBlob) {
      return;
    }
    await updateProfilePicture(imgBlob);
    handleCancel();
  }

  function handleCancel() {
    setNewImage(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onFileChange}
      />
      <button
        type="button"
        className="relative isolate"
        aria-label={t('Common.UpdatePersonalInfo.EditPicture')}
        data-tid="button-edit-picture"
        onClick={openFilePicker}
      >
        {children}
        <div className="absolute bottom-0 right-0 z-10 grid size-6 place-items-center rounded-full border border-nos-neutral-100 bg-nos-neutral-0 text-nos-neutral-1000">
          <Pencil width={16} height={16} />
        </div>
      </button>
      <Dialog
        open={!!newImage || isPending}
        title={t('Common.UpdatePersonalInfo.CropPictureDialog.Title')}
        skin="light"
        overlayClassName="z-[60]"
        className="z-[60]"
        setOpen={(o) => {
          if (o) return;

          handleCancel();
        }}
      >
        <div className="flex flex-col items-center gap-16 pt-6">
          <div className="flex w-full flex-col items-center gap-4">
            <Cropper
              image={newImage || ''}
              onCropComplete={handleCropComplete}
              crop={crop}
              onCropChange={setCrop}
              zoom={zoom}
              onZoomChange={setZoom}
              aspect={1}
              onRotationChange={setRotation}
              rotation={rotation}
              cropShape="round"
              showGrid={false}
              zoomWithScroll={false}
              restrictPosition={false}
              cropSize={{ width: 223, height: 223 }}
              style={{
                containerStyle: { position: 'relative' },
              }}
              classes={{
                containerClassName: 'size-[223px] rounded-full bg-nos-neutral-1000',
                cropAreaClassName: '!border-2 !border-nos-neutral-1000',
              }}
              objectFit="cover"
            />
            <Range
              className="w-full"
              id="zoom-range"
              skin="light"
              label="Common.UpdatePersonalInfo.CropPictureDialog.Zoom"
              min={0}
              max={2}
              value={[zoom]}
              step={0.1}
              onValueChange={([value]) => {
                setZoom(value);
              }}
            />
            <Range
              className="w-full"
              id="rotation-range"
              skin="light"
              label="Common.UpdatePersonalInfo.CropPictureDialog.Rotation"
              min={-180}
              max={180}
              value={[rotation]}
              step={1}
              onValueChange={([value]) => {
                setRotation(value);
              }}
            />
            <div className="py-4">
              <button
                type="button"
                onClick={handleSnapRotation}
                data-tid="button-rotate"
                className="grid h-[66px] min-w-[71px] place-items-center gap-1 rounded-lg border border-nos-neutral-1000 px-4 pb-2 pt-3 font-medium text-nos-neutral-900 font-text-p2"
              >
                <Restart width={24} height={24} className="text-nos-neutral-1000" />
                {t('Common.UpdatePersonalInfo.CropPictureDialog.Rotate')}
              </button>
            </div>
          </div>
          <div className="mt-auto grid w-full gap-4 tablet:grid-cols-2">
            <Button
              skin="light"
              variant="secondary"
              onClick={handleCancel}
              className="flex-1"
              disabled={isPending}
              data-tid="button-cancel-crop"
            >
              {t('Common.UpdatePersonalInfo.CropPictureDialog.Cancel')}
            </Button>
            <Button
              skin="light"
              onClick={handleConfirm}
              className="flex-1"
              disabled={isPending}
              data-tid="button-confirm-crop"
            >
              {isPending ? (
                <Loader width={24} height={24} />
              ) : (
                t('Common.UpdatePersonalInfo.CropPictureDialog.Confirm')
              )}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
